/**
 * Configurator domain API client.
 *
 * Named exports covering sixteen endpoints across six concern areas:
 * shapes, product-type configurator config, contour job pipeline,
 * custom shape persistence, customer design persistence, design templates,
 * and proof approve/revise actions.
 *
 * Each function:
 *   1. Calls the configured shared Axios instance with the correct path,
 *      method, and body.
 *   2. Unwraps the 2xx envelope into the typed DTO.
 *   3. Catches any Axios rejection and rethrows as {@link ApiError},
 *      preserving `status`, `severity`, `message`, `errors`, and
 *      `errorCode` from the platform envelope.
 *
 * Authentication relies solely on the Bearer-token interceptor already
 * wired in `client.ts` — no auth logic lives here.
 *
 * **BACKED vs PENDING**
 *
 * BACKED (real platform endpoints, wire now):
 *   - `fetchShape(id)` — GET /api/v1/shapes/{id}
 *   - `fetchConfiguratorConfig(productType)` — GET /api/v1/product-types/{type}/configurator
 *
 * BACKED (real platform endpoints, shipped Phase 10):
 *   - `saveCustomShape(payload)` — POST /api/v1/custom-shapes (PL-SHAPED-003)
 *   - `updateCustomShape(id, payload)` — PUT /api/v1/custom-shapes/{id} (PL-SHAPED-004)
 *   - `loadCustomShape(id)` — GET /api/v1/custom-shapes/{id} (PL-SHAPED-004)
 *
 * PENDING (typed, compile correctly; 404 until platform ships them):
 *   - `fetchShapes()` — GET /api/v1/shapes — no public list endpoint yet (Phase 10)
 *   - `fetchShapeSizes(shapeId, productType)` — GET /api/v1/shapes/{id}/sizes (Phase 10)
 *   - `downloadShapeTemplate(shapeId, sizeId)` — GET /api/v1/shapes/{id}/template (Phase 10)
 *   - `saveCustomerDesign(payload)` — POST /api/v1/designs (Phase 10/11)
 *   - `updateCustomerDesign(id, payload)` — PUT /api/v1/designs/{id} (Phase 10/11)
 *   - `loadCustomerDesign(id)` — GET /api/v1/designs/{id} (Phase 10/11)
 *   - `listDesignTemplates(filters?)` — GET /api/v1/design-templates (Phase 10/11)
 *   - `approveProof(id)` — POST /api/v1/proofs/{id}/approve (Phase 10/11)
 *   - `requestProofRevision(id, notes)` — POST /api/v1/proofs/{id}/revise (Phase 10/11)
 *
 * SF-CONFIG-002. Consumed by SF-CONFIG-003 (Redux slice), SF-CONFIG-004
 * (wizard), SF-CONFIG-005 (contour upload), SF-CONFIG-006 (custom shape
 * editor), SF-CONFIG-007 (online designer).
 */
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  ConfiguratorConfigDTO,
  CustomShapePathDTO,
  SaveCustomShapePayload,
  UpdateCustomShapePayload,
} from '@dfq/shared/types/configurator';

import { apiClient } from './client';
import { throwAsApiError, unwrap } from './_envelope';

// ============================================================================
// Configurator config
// ============================================================================


// ============================================================================
// PENDING — shape list — Phase 10 (no public list endpoint yet)
// ============================================================================


// ============================================================================
// PENDING — shape sizes — Phase 10
// ============================================================================


// ============================================================================
// PENDING — shape template download — Phase 10
// ============================================================================


// ============================================================================
// BACKED — configurator config
// ============================================================================

/**
 * `GET /api/v1/product-types/{type}/configurator` — PUBLIC, unauthenticated.
 *
 * Returns the full step-by-step configurator configuration for the given
 * product type slug, including step sequence, available shapes, active
 * assigned materials, option groups, pricing tiers, default proof type,
 * reprocessing fee, and default file rules.
 *
 * 404 is thrown as {@link ApiError} when no active step-by-step config
 * exists for the slug.
 *
 * @param productType - Product type slug (e.g. `stickers`, `banners`).
 * @returns The {@link ConfiguratorConfigDTO} for the requested product type.
 */
export async function fetchConfiguratorConfig(
  productType: string,
): Promise<ConfiguratorConfigDTO> {
  const type = encodeURIComponent(productType);
  try {
    const response = await apiClient.get<ApiEnvelope<{ configurator: ConfiguratorConfigDTO }>>(
      `/product-types/${type}/configurator`,
    );
    return unwrap(response.data, response.status).configurator;
  } catch (error) {
    throwAsApiError(error);
  }
}

// ============================================================================
// PENDING — custom shapes — Phase 10
// ============================================================================

/**
 * `POST /api/v1/custom-shapes`
 *
 * Saves a new custom shape path created or edited in the shape editor.
 * Backed by PL-SHAPED-003 (`POST /api/v1/custom-shapes`).
 *
 * @param payload - Custom shape creation parameters.
 * @returns The persisted {@link CustomShapePathDTO}.
 */
export async function saveCustomShape(
  payload: SaveCustomShapePayload,
): Promise<CustomShapePathDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<CustomShapePathDTO>>(
      '/custom-shapes',
      payload,
    );
    return unwrap(response.data, response.status);
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `PUT /api/v1/custom-shapes/{id}`
 *
 * Updates an existing custom shape path. The shape ID is encoded in the URL;
 * it is not included in the request body. Backed by PL-SHAPED-004
 * (`PUT /api/v1/custom-shapes/{id}`).
 *
 * @param id - Custom shape surrogate primary key.
 * @param payload - Fields to update.
 * @returns The updated {@link CustomShapePathDTO}.
 */
export async function updateCustomShape(
  id: number,
  payload: UpdateCustomShapePayload,
): Promise<CustomShapePathDTO> {
  try {
    const response = await apiClient.put<ApiEnvelope<CustomShapePathDTO>>(
      `/custom-shapes/${encodeURIComponent(String(id))}`,
      payload,
    );
    return unwrap(response.data, response.status);
  } catch (error) {
    throwAsApiError(error);
  }
}


// ============================================================================
// PENDING — customer designs — Phase 10/11
// ============================================================================




// ============================================================================
// PENDING — design templates — Phase 10/11
// ============================================================================


// ============================================================================
// PENDING — proofs — Phase 10/11
// ============================================================================


